import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Awareness } from "y-protocols/awareness";
import * as Y from "yjs";

declare global {
  var __vttYjsContext:
    | ReturnType<
        typeof createContext<{ doc: Y.Doc; provider: any; room: string } | null>
      >
    | undefined;
  var __vttYjsStatusContext:
    | ReturnType<typeof createContext<string>>
    | undefined;
  var __vttYjsAwarenessContext:
    | ReturnType<typeof createContext<any>>
    | undefined;
}

/**
 * Generates a random room ID.
 * @returns A random 8-character alphanumeric string
 */
function generateRoomId(): string {
  return Math.random().toString(36).substring(2, 10);
}

/**
 * Gets or creates a room ID from the URL.
 * - If `?room=xxx` is in URL, use that value
 * - Otherwise generate a new UUID and update the URL
 */
function getOrCreateRoomId(): string {
  const params = new URLSearchParams(window.location.search);
  let roomId = params.get("room");

  if (!roomId) {
    roomId = generateRoomId();
    const newUrl = `${window.location.pathname}?room=${roomId}${window.location.hash}`;
    window.history.replaceState(null, "", newUrl);
  }

  return roomId;
}

const YjsContext =
  globalThis.__vttYjsContext ??
  createContext<{ doc: Y.Doc; provider: any; room: string } | null>(null);
const YjsStatusContext =
  globalThis.__vttYjsStatusContext ?? createContext<string>("offline-local");
const YjsAwarenessContext =
  globalThis.__vttYjsAwarenessContext ?? createContext<any>(null);

globalThis.__vttYjsContext = YjsContext;
globalThis.__vttYjsStatusContext = YjsStatusContext;
globalThis.__vttYjsAwarenessContext = YjsAwarenessContext;

/** Generate a random user color */
function generateUserColor(): string {
  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEAA7",
    "#DDA0DD",
    "#98D8C8",
    "#F7DC6F",
    "#BB8FCE",
    "#85C1E9",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

/** Generate a random user name */
function generateUserName(): string {
  const adjectives = ["Swift", "Brave", "Clever", "Mystic", "Noble"];
  const nouns = ["Dragon", "Phoenix", "Wolf", "Eagle", "Bear"];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  return `${adj} ${noun}`;
}

/**
 * Provides a shared Yjs document to the component tree.
 *
 * In dev/offline mode: creates a local-only Y.Doc.
 * When VITE_PARTYKIT_HOST is set: connects via y-partykit for multiplayer sync.
 */
export function YjsProvider({ children }: { children: React.ReactNode }) {
  const docRef = useRef<Y.Doc | null>(null);
  const providerRef = useRef<any>(null);
  const awarenessRef = useRef<any>(null);
  const [room, setRoom] = useState<string>("");
  const [status, setStatus] = useState("offline-local");
  // Force a re-render when provider connects so deeply nested hooks get the constructed provider
  const [, forceRender] = useState(0);

  // Initialize room ID on first render
  if (!room) {
    const roomId = getOrCreateRoomId();
    setRoom(roomId);
  }

  if (!docRef.current) {
    docRef.current = new Y.Doc();
  }

  useEffect(() => {
    // Set up local awareness (works with or without network provider)
    const awareness = new Awareness(docRef.current!);
    awarenessRef.current = awareness;

    // Set local user state
    const userColor = generateUserColor();
    const userName = generateUserName();
    awareness.setLocalState({
      user: { name: userName, color: userColor },
      cursor: null,
    });

    const host = import.meta.env.VITE_PARTYKIT_HOST;
    let isDisposed = false;

    if (host && room) {
      setStatus("connecting");
      import("y-partykit/provider").then((mod) => {
        if (isDisposed) return;
        const provider = new mod.default(host, room, docRef.current!) as any;

        // Connect awareness to provider
        provider.awareness = awareness;

        provider.on?.("status", (event: any) => {
          setStatus(event.status);
        });

        providerRef.current = provider;
        forceRender((n) => n + 1); // trigger pass to context
      });
    } else {
      // Local-only mode: still set status to connected without network
      setStatus("connected-local");
    }

    return () => {
      isDisposed = true;
      awareness.setLocalState(null);
      providerRef.current?.destroy();
      providerRef.current = null;
      awarenessRef.current = null;
      setStatus("offline-local");
    };
  }, [room]);

  return (
    <YjsContext.Provider
      value={{ doc: docRef.current, provider: providerRef.current, room }}
    >
      <YjsStatusContext.Provider value={status}>
        <YjsAwarenessContext.Provider value={awarenessRef.current}>
          {children}
        </YjsAwarenessContext.Provider>
      </YjsStatusContext.Provider>
    </YjsContext.Provider>
  );
}

export function useYDoc() {
  const ctx = useContext(YjsContext);
  if (!ctx) throw new Error("useYDoc must be used within <YjsProvider>");
  return ctx.doc;
}

export function useYjsProvider() {
  const ctx = useContext(YjsContext);
  if (!ctx) throw new Error("useYDoc must be used within <YjsProvider>");
  return ctx.provider;
}

export function useYjsStatus() {
  return useContext(YjsStatusContext);
}

export function useYjsAwareness() {
  return useContext(YjsAwarenessContext);
}

export function useYjsRoom() {
  const ctx = useContext(YjsContext);
  if (!ctx) throw new Error("useYjsRoom must be used within <YjsProvider>");
  return ctx.room;
}

/**
 * Hook to update the local user's cursor position in awareness.
 * Call this with the current cursor position to broadcast to other users.
 */
export function useUpdateCursor() {
  const awareness = useYjsAwareness();

  return (position: { x: number; z: number } | null) => {
    if (!awareness) return;

    const currentState = awareness.getLocalState() as {
      user: { name: string; color: string };
      cursor: any;
    } | null;

    if (currentState) {
      awareness.setLocalState({
        ...currentState,
        cursor: position,
      });
    }
  };
}
